import { splitRecord } from '@common/record';
import { buildDisplaySeason, getPreviousSeason, getCurrentSeason, getNextSeason } from '@common/season';
import { formatSport } from '@common/sport';
import { abbreviateName } from '@common/team';
import { formatTeamNameForUrl } from '@util/format-team-name-for-url';
import { isLiteralType } from '@util/types';
import { getFeatureFlag } from '@util/user/featureFlags';
import type { PublicTeam, Team } from './types';
import { NGB, Season, Sport } from './types';

export { buildDisplaySeason, getPreviousSeason, getCurrentSeason, getNextSeason, abbreviateName };
export { formatSport };

const SeasonSortOrder: { [key in Season]: number } = {
    [Season.WINTER]: 0,
    [Season.FALL]: 1,
    [Season.SUMMER]: 2,
    [Season.SPRING]: 3,
};

export const compareSeasons = (a: Season, b: Season) => SeasonSortOrder[a] - SeasonSortOrder[b];

/**
 * Map Team to PublicTeam, or undefined if the Team has no publicID
 * @param team
 */
export const mapTeamToPublicTeam = (team: Team): PublicTeam | undefined => {
    if (team.publicID) {
        const record = splitRecord(team.seasonRecord);

        return {
            id: team.publicID,
            name: team.name,
            urlEncodedName: formatTeamNameForUrl({
                name: team.name,
                seasonName: team.seasonName,
                seasonYear: team.seasonYear,
            }),
            sport: team.sport,
            avatarUrl: team.avatar_url ?? '',
            location: {
                city: team.city ?? undefined,
                state: team.state ?? undefined,
                country: team.country ?? undefined,
            },
            seasonName: team.seasonName,
            seasonYear: team.seasonYear,
            seasonRecord: record,
            ageGroup: team.ageGroup,
        };
    }
    return undefined;
};

const ngbTitle: Record<NGB, string> = {
    [NGB.AAU]: 'AAU',
    [NGB.ALLIANCE_FASTPITCH]: 'Alliance FastPitch',
    [NGB.AMERICAN_LEGION]: 'American Legion',
    [NGB.BABE_RUTH_CAL_RIPKEN_LEAGUE]: 'Babe Ruth League/Cal Ripken',
    [NGB.LITTLE_LEAGUE]: 'Little League',
    [NGB.PERFECT_GAME]: 'Perfect Game',
    [NGB.PONY]: 'PONY',
    [NGB.PREMIER_GIRLS_FASTPITCH]: 'Premier Girls Fastpitch',
    [NGB.TRIPPLE_CROWN]: 'Triple Crown',
    [NGB.USA_SOFTBALL]: 'USA Softball',
    [NGB.USSSA]: 'USSSA',
    [NGB.MLB_RBI]: 'MLB RBI',
};

const nationalGoverningBodiesBySport: Record<string, NGB[]> = {
    [Sport.BASEBALL]: [
        NGB.LITTLE_LEAGUE,
        NGB.BABE_RUTH_CAL_RIPKEN_LEAGUE,
        NGB.USSSA,
        NGB.PONY,
        NGB.AMERICAN_LEGION,
        NGB.PERFECT_GAME,
        NGB.AAU,
        NGB.MLB_RBI,
    ],
    [Sport.SOFTBALL]: [
        NGB.LITTLE_LEAGUE,
        NGB.USA_SOFTBALL,
        NGB.BABE_RUTH_CAL_RIPKEN_LEAGUE,
        NGB.USSSA,
        NGB.PONY,
        NGB.TRIPPLE_CROWN,
        NGB.PREMIER_GIRLS_FASTPITCH,
        NGB.ALLIANCE_FASTPITCH,
        NGB.PERFECT_GAME,
    ],
};

export const getNationalGoverningBodyOptions = (sport?: Sport): { name: string; value: string }[] | undefined => {
    if (!sport) return;
    const ngbs = nationalGoverningBodiesBySport[sport];
    const enableMlbRbi = getFeatureFlag('enableMlbRbiNgb');
    const filteredNgbs = ngbs?.filter((ngb) => enableMlbRbi || ngb !== NGB.MLB_RBI);
    return filteredNgbs?.map((ngb) => ({ name: ngbTitle[ngb], value: ngb }));
};

export const parseNGB = (ngbString: string | undefined): NGB[] | undefined => {
    if (!ngbString) return undefined;
    try {
        const ngbArray = JSON.parse(ngbString);
        if (!Array.isArray(ngbArray)) {
            return undefined;
        }

        const filteredNGBArray = ngbArray.filter((ngb) => isNGB(ngb));

        return filteredNGBArray as NGB[];
    } catch (e) {
        return undefined;
    }
};

export const isNGB = (str: string): str is NGB => isLiteralType(str, Object.values(NGB));
